import { buildDoubleArrowRing, createLocalProjection, } from "@plotlibre/geometry";
import { createRenderProperties, DEFAULT_ARROW_STYLE } from "./style.js";
export const DOUBLE_ARROW_TYPE = "arrow.double";
export const doubleArrowDefinition = {
    type: DOUBLE_ARROW_TYPE,
    title: "Double Arrow",
    category: "arrow",
    version: "1.0.0",
    controlSchema: {
        minPoints: 4,
        maxPoints: 4,
        completeOnDoubleClick: false,
        allowPointInsertion: false,
        allowPointRemoval: false,
    },
    defaultParameters: {
        branchPositionRatio: 0.42,
        headLengthRatio: 0.22,
        maximumHeadLengthTailRatio: 2.2,
        headHalfWidthTailRatio: 0.58,
        neckHalfWidthTailRatio: 0.18,
        bodyBulgeRatio: 1.05,
        innerBridgeRatio: 0.55,
        tension: 0.18,
        segmentsPerSpan: 12,
        miterLimit: 3,
        minimumTailWidthMeters: 1,
        maximumTailWidthMeters: 100_000,
    },
    defaultStyle: DEFAULT_ARROW_STYLE,
    deriveDraftControlPoints(controlPoints) {
        return deriveDoubleArrowDraftControlPoints(controlPoints);
    },
    generate({ feature }) {
        return generateDoubleArrow(feature);
    },
    validate({ feature }) {
        try {
            buildDoubleArrowRing(feature.controlPoints, readParameters({
                ...doubleArrowDefinition.defaultParameters,
                ...feature.parameters,
            }));
            return { valid: true, issues: [] };
        }
        catch (error) {
            return {
                valid: false,
                issues: [
                    {
                        code: "INVALID_DOUBLE_ARROW_GEOMETRY",
                        message: error instanceof Error
                            ? error.message
                            : "Double arrow geometry is invalid.",
                        severity: "error",
                    },
                ],
            };
        }
    },
};
/**
 * Builds a transient fourth objective for the three-click drawing state.
 *
 * The first objective is reflected across the forward axis that passes through
 * the tail midpoint and is perpendicular to the tail baseline. Projection and
 * reflection are performed in local metres. When the authored objective is on
 * or very near that axis, the preview receives a deterministic minimum lateral
 * spread so the third click still produces a renderable compound-arrow draft.
 * The returned fourth point is only a draft convenience; both objectives remain
 * explicit authored controls in canonical PlotJSON and Store state.
 */
export function deriveDoubleArrowDraftControlPoints(controlPoints) {
    if (controlPoints.length !== 3)
        return undefined;
    const tailA = controlPoints[0];
    const tailB = controlPoints[1];
    const objectiveA = controlPoints[2];
    if (!tailA || !tailB || !objectiveA)
        return undefined;
    try {
        const projection = createLocalProjection(tailA);
        const tailBLocal = projection.project(tailB);
        const objectiveLocal = projection.project(objectiveA);
        const baselineLength = Math.hypot(tailBLocal.x, tailBLocal.y);
        if (!Number.isFinite(baselineLength) || baselineLength <= 1e-6) {
            return undefined;
        }
        const lateralUnit = {
            x: tailBLocal.x / baselineLength,
            y: tailBLocal.y / baselineLength,
        };
        const tailCenter = {
            x: tailBLocal.x / 2,
            y: tailBLocal.y / 2,
        };
        const objectiveDelta = {
            x: objectiveLocal.x - tailCenter.x,
            y: objectiveLocal.y - tailCenter.y,
        };
        const unsignedForward = {
            x: -lateralUnit.y,
            y: lateralUnit.x,
        };
        const forwardProjection = dot(objectiveDelta, unsignedForward);
        const forwardSign = forwardProjection >= 0 ? 1 : -1;
        const forwardUnit = {
            x: unsignedForward.x * forwardSign,
            y: unsignedForward.y * forwardSign,
        };
        const forwardDistance = dot(objectiveDelta, forwardUnit);
        const lateralDistance = dot(objectiveDelta, lateralUnit);
        if (!Number.isFinite(forwardDistance) ||
            !Number.isFinite(lateralDistance) ||
            forwardDistance <= Math.max(1, baselineLength * 0.5)) {
            return undefined;
        }
        const minimumPreviewLateralDistance = Math.max(1, baselineLength * 1.25, Math.min(forwardDistance * 0.35, baselineLength * 3));
        const previewLateralSign = Math.abs(lateralDistance) > 1e-9 ? Math.sign(lateralDistance) : 1;
        const previewLateralDistance = previewLateralSign *
            Math.max(Math.abs(lateralDistance), minimumPreviewLateralDistance);
        const mirroredObjective = projection.unproject({
            x: tailCenter.x +
                forwardUnit.x * forwardDistance -
                lateralUnit.x * previewLateralDistance,
            y: tailCenter.y +
                forwardUnit.y * forwardDistance -
                lateralUnit.y * previewLateralDistance,
        });
        return [
            clonePosition(tailA),
            clonePosition(tailB),
            clonePosition(objectiveA),
            clonePosition(mirroredObjective),
        ];
    }
    catch {
        return undefined;
    }
}
export function generateDoubleArrow(feature) {
    if (feature.controlPoints.length !== 4) {
        throw new RangeError("Double arrow requires exactly four control points.");
    }
    const parameters = readParameters({
        ...doubleArrowDefinition.defaultParameters,
        ...feature.parameters,
    });
    const ring = buildDoubleArrowRing(feature.controlPoints, parameters);
    const geometry = {
        type: "Polygon",
        coordinates: [ring],
    };
    return {
        fills: [
            {
                type: "Feature",
                id: `${feature.id}:fill`,
                geometry,
                properties: createRenderProperties(feature, "fill", doubleArrowDefinition.defaultStyle),
            },
        ],
        lines: [
            {
                type: "Feature",
                id: `${feature.id}:outline`,
                geometry,
                properties: createRenderProperties(feature, "outline", doubleArrowDefinition.defaultStyle),
            },
        ],
        points: [],
        labels: [],
        hitAreas: [
            {
                type: "Feature",
                id: `${feature.id}:hit-area`,
                geometry,
                properties: createRenderProperties(feature, "hit-area", doubleArrowDefinition.defaultStyle),
            },
        ],
    };
}
function readParameters(parameters) {
    return {
        branchPositionRatio: readNumber(parameters, "branchPositionRatio"),
        headLengthRatio: readNumber(parameters, "headLengthRatio"),
        maximumHeadLengthTailRatio: readNumber(parameters, "maximumHeadLengthTailRatio"),
        headHalfWidthTailRatio: readNumber(parameters, "headHalfWidthTailRatio"),
        neckHalfWidthTailRatio: readNumber(parameters, "neckHalfWidthTailRatio"),
        bodyBulgeRatio: readNumber(parameters, "bodyBulgeRatio"),
        innerBridgeRatio: readNumber(parameters, "innerBridgeRatio"),
        tension: readNumber(parameters, "tension"),
        segmentsPerSpan: readNumber(parameters, "segmentsPerSpan"),
        miterLimit: readNumber(parameters, "miterLimit"),
        minimumTailWidthMeters: readNumber(parameters, "minimumTailWidthMeters"),
        maximumTailWidthMeters: readNumber(parameters, "maximumTailWidthMeters"),
    };
}
function readNumber(parameters, key) {
    const value = parameters[key];
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new TypeError(`Double arrow parameter "${key}" must be finite.`);
    }
    return value;
}
function dot(left, right) {
    return left.x * right.x + left.y * right.y;
}
function clonePosition([longitude, latitude]) {
    return [longitude, latitude];
}
//# sourceMappingURL=double-arrow.js.map